import { defineStore } from "pinia";
import productImage from "/images/product_categories/OTROS.png";
import { useStoresStore } from "@/stores/apps/store";
import successBeep from "/public/sounds/snapei-beep-scan.mp3";
import popBeep from "/public/sounds/snapei-pop.mp3";
import beepAudio from "/public/sounds/snapei-beep.mp3";
import alertAudio from "/public/sounds/snapei-alert-beep.mp3";
import { generateUUID } from "@/utils/format";
import { getCachedImage, cacheImage } from "@/utils/helpers/indexedDBHelper";
import { Chance } from "chance";
const chance = new Chance();
import { sum } from "lodash";

// project imports
import axios from "@/utils/axios";
// types
import type { ProductStateProps, Products } from "@/types/apps/EcommerceType";
import { recategorizeData, categoriesImages } from "@/types/apps/EcommerceType";
import { filter, map } from "lodash";

export const useEcomStore = defineStore({
    id: "eCommerce",
    state: (): ProductStateProps => ({
        cart: <Products[]>[],
        products: [],
        categories: [],
        price: null,
        price_discount: 0,
        discount: 0,
        discount_card: 0,
        discount_percent: 0,
        taxes: 0,
        total: 0,
        subTotal: 0,
        total_qty: 0,
        activeCard: null,
        paymentMethod: null,
        hasRestriction: false,
        hasPriceRestriction: false,
        hasGlobalPromotion: false,
        startTime: null,

        category: [],
        color: "All",
        gender: "",
        page: 1,
        pageSize: 20,
        pageCount: 1,
        totalRows: 0,
        filterDiscount: false,
        sortBy: "price" as string,
        search: null,
        stock: true,
        layout: "grid",
        isProductImageDisplayed: true,
    }),
    getters: {
        getTotals(state) {
            return {
                total: state.total,
                subtotal: state.subTotal,
                discount: state.discount,
                discount_card: state.discount_card,
                discount_percent: state.discount_percent,
                taxes: state.taxes,
                qty: state.total_qty,
            };
        },
        getCart(state) {
            return state.cart;
        },
        getTotalQty(state) {
            return state.total_qty ?? 0;
        },
    },
    actions: {
        async fetchProducts(
            this: ProductStateProps,
            params: {
                page: number;
                page_size: number;
                category_in?: string;
                min_price?: string;
                max_price?: string;
                has_discount?: boolean | null;
                has_stock?: boolean | null;
                ordering?: string | null;
                search?: string;
            } = { page: this.page, page_size: this.pageSize },
        ) {
            if (this.category && this.category.length > 0) {
                params["category_in"] = this.category.join(",");
            }
            if (this.price) {
                params["min_price"] = this.price.split(":")[0];
                params["max_price"] = this.price.split(":")[1];
            }
            if (this.filterDiscount !== null) {
                params["has_discount"] = this.filterDiscount;
            }
            if (this.stock !== null) {
                params["has_stock"] = this.stock;
            }
            if (this.sortBy) {
                params["ordering"] = this.sortBy;
            }
            if (this.search) {
                params["search"] = this.search.toUpperCase().trim();
            }
            // console.log("Filtered... parameters: ", params);

            try {
                // const data = await axios.get('/api/products/list', { params });
                // const storeId = useStoresStore().selectedStore.id;
                // # TODO: This should be the store ID, not the default store
                const baseUrl = useRuntimeConfig().public.apiBase;
                const queryString = new URLSearchParams(
                    params as any,
                ).toString();
                let response = await fetchWrapper.get(
                    `${baseUrl}/api/v1/management/stores/1/products/?${queryString}`,
                );
                for (const item of response.results as Products[]) {
                    // # TODO: To image caché from indexedDB.... (errors with redirects 😭)
                    // if (item.image) {
                    //     const cachedImage = await getCachedImage(item.id);
                    //     if (cachedImage) {
                    //         item.image = URL.createObjectURL(cachedImage);
                    //         console.log("Cached Image URL:", item.id, item.image);
                    //     } else {
                    //         console.log("No cached image found for product ID: ", item.id, " .. caching...");
                    //         item.image = await cacheImage(item);
                    //     }
                    // }

                    const findCategory = recategorizeData.find(
                        (data) => data.category === item.category,
                    );
                    const newCategory =
                        findCategory && findCategory.group
                            ? findCategory.group
                            : item.category;
                    const findImage = categoriesImages.find(
                        (data) => data.category === newCategory,
                    );
                    const categoryImage =
                        findImage && findImage.image
                            ? findImage.image
                            : productImage;
                    item.image = item.image ? item.image : categoryImage;
                    item.categoryImage = categoryImage;
                    item.categories = [newCategory];
                    item.rating = item.rating
                        ? item.rating
                        : chance.floating({ min: 0.1, max: 5.0 });
                    item.popularity = item.popularity
                        ? item.popularity
                        : chance.natural();
                    item.rank = chance
                        .floating({ min: 0, max: 150 })
                        .toFixed(0);
                    item.isStock = item.stock && item.stock > 0 ? true : false;

                    // Inicializar valores por defecto
                    item.qty = 0;
                    item.added = null;
                    item.pk = undefined;

                    // Buscar y sincronizar con el carrito de manera reactiva
                    if (this.cart?.length > 0) {
                        console.log("buscando item en carro: ", item.id);
                        const findItemInCart = async (
                            item: Products,
                        ): Promise<Products | undefined> => {
                            return await this.cart.find(
                                (cartItem: Products) => cartItem.id === item.id,
                            );
                        };

                        try {
                            const itemFromCart = await findItemInCart(item);
                            if (itemFromCart) {
                                console.log(
                                    "item encontrado en carro:",
                                    itemFromCart,
                                );
                                Object.assign(item, {
                                    added: itemFromCart.added ?? null,
                                    pk: itemFromCart.pk ?? undefined,
                                    qty: itemFromCart.qty ?? 0,
                                });
                            }
                        } catch (error) {
                            console.error("Error syncing cart item:", error);
                        }
                    }
                }
                console.log(
                    "Products:",
                    response.results.length,
                    " rows to update...",
                );
                this.products = response.results;
                this.pageCount = Math.round(
                    response.count / (params.page_size ?? 1),
                );
                this.totalRows = response.count;
                return response;
            } catch (error) {
                // alert(error);
                console.log(error);
            }
        },

        async fetchCategories(params = { page: 1, pageSize: 50 }) {
            try {
                const data = await axios.get("/api/categories/list", {
                    params,
                });
                var new_categories = [];
                for (var category of data.data.results) {
                    const findCategory = recategorizeData.find(
                        (data) => data.category === category,
                    );
                    new_categories.push(
                        findCategory && findCategory.group
                            ? findCategory.group
                            : category,
                    );
                }
                this.categories = Array.from(new Set(new_categories)).sort();
                // this.categories = data.data.results;
                return data;
            } catch (error) {
                // alert(error);
                console.log(error);
            }
        },

        async setFilterProductsAdded(state: Boolean) {
            if (state) {
                this.products = this.cart;
                this.totalRows = this.cart.length;
                this.page = 1;
                this.pageSize = Math.round((this.cart.length + 10) / 20) * 20;
                this.pageCount = 1;
            } else {
                await this.fetchProducts();
            }
        },

        async setIsProductImageDisplayed(state: boolean) {
            this.isProductImageDisplayed = state;
        },

        async updateItem(item: Products) {
            try {
                const updatedProduct = await this.fetchProduct(item, true);
                Object.assign(item, updatedProduct, {
                    added: new Date(),
                });
            } catch (err) {
                this.deleteProduct(item.pk);
            }
        },
        async validateProducts() {
            const today = new Date().toISOString().split("T")[0]; // .replace(/\D/g, '');
            for (const item of this.cart) {
                if (
                    (typeof item.added === "object"
                        ? item.added.toISOString()
                        : item.added
                    ).split("T")[0] !== today
                ) {
                    await this.updateItem(item);
                }
            }
            return this.cart;
        },
        // Fetch product
        async fetchProduct(
            product: string | number | Products,
            returnItem?: boolean,
            quantity?: number,
            fromRecover?: boolean,
        ) {
            // console.log("Fetch Product:", product, typeof product)
            if (typeof product === "undefined") {
                console.error(
                    "Product is undefined!!",
                    typeof product,
                    product,
                );
                // TODO: Mensaje de error advirtiendo que no llegó el item (esto no debería suceder)
                throw new Error("Product is undefined!");
            } else if (
                typeof product === "string" ||
                typeof product === "number"
            ) {
                if (typeof product === "string" && product.trim() === "") {
                    console.error(
                        "Product is empty!!",
                        typeof product,
                        product,
                    );
                    throw new Error("Product is empty!");
                }
                let founded = false;
                const updateCart = map(this.cart, (item: any) => {
                    if (item.barcode === product && item.unit !== "KG") {
                        founded = true;
                        if (returnItem) {
                            return item;
                        }
                        return {
                            ...item,
                            qty: item.qty + 1,
                        };
                    }
                    return item;
                });
                if (founded) {
                    this.cart = updateCart;
                    const sound = new Audio(successBeep);
                    sound
                        .play()
                        .catch((error) =>
                            console.error(
                                "Error al reproducir el sonido:",
                                error,
                            ),
                        );
                    this.updateTotals();
                    return true;
                }
            }
            const baseUrl = useRuntimeConfig().public.apiBase;
            const storeId = useStoresStore().selectedStore.id;
            try {
                let response = await fetchWrapper.get(
                    `${baseUrl}/api/v1/management/stores/${storeId}/products/${typeof product === "string" || typeof product === "number" ? product : product.barcode}?_=${new Date().getTime()}`,
                );
                if (response) {
                    const findCategory = recategorizeData.find(
                        (data) => data.category === response.category,
                    );
                    const newCategory =
                        findCategory && findCategory.group
                            ? findCategory.group
                            : response.category;
                    const findImage = categoriesImages.find(
                        (data) => data.category === newCategory,
                    );
                    const categoryImage =
                        findImage && findImage.image
                            ? findImage.image
                            : productImage;

                    // aquí aplico los descuentos distintos, para luego aplicar al precio el mayor
                    let price_with_discount =
                        parseFloat(response.discount) > 0
                            ? parseFloat(response.price) *
                              (1 - parseFloat(response.discount) / 100)
                            : response.price;
                    let price_with_price_discount =
                        parseFloat(response.price_discount) > 0
                            ? parseFloat(response.price) -
                              parseFloat(response.price_discount)
                            : response.price;

                    let offer_price =
                        price_with_discount < price_with_price_discount
                            ? price_with_discount
                            : price_with_price_discount;
                    let offer_type =
                        price_with_discount < price_with_price_discount
                            ? "discount"
                            : offer_price !== response.price
                              ? "price_discount"
                              : "";

                    const productFound = {
                        pk: generateUUID(),
                        id: response.id,
                        sku: response.sku,
                        barcode: response.ean,
                        readed_code: response.ean,
                        name: response.name,
                        categories: [findCategory?.group],
                        price: Math.round(response.price),
                        offer_price: Math.round(offer_price),
                        offer_type: offer_type,
                        offer_card_price: Math.round(
                            parseFloat(response.discount_card) -
                                parseFloat(response.discount) >
                                0
                                ? parseFloat(response.price) *
                                      (1 -
                                          (parseFloat(response.discount_card) -
                                              parseFloat(response.discount)) /
                                              100)
                                : 0,
                        ),
                        qty: quantity ?? 1,
                        tax: parseFloat(response.iva),
                        unit: response.und.toUpperCase().trim(),
                        price_discount:
                            parseFloat(response.price_discount) ?? 0,
                        discount: parseFloat(response.discount) ?? 0,
                        discount_card: parseFloat(response.discount_card) ?? 0,
                        discount_percent:
                            parseFloat(response.discount_percent) ?? 0,
                        age_restriction: response.age_restriction ?? false,
                        image: categoryImage,
                        applied_promos: [],
                        promo_discount: 0,
                        promo_price: 0,
                        added: new Date(),
                    };

                    if (
                        typeof product === "string" ||
                        typeof product === "number"
                    ) {
                        if (returnItem) {
                            return productFound;
                        }
                        return this.addToCart(productFound, fromRecover);
                    } else {
                        const sound = new Audio(alertAudio);
                        sound
                            .play()
                            .catch((error) =>
                                console.error(
                                    "Error al reproducir el sonido:",
                                    error,
                                ),
                            );
                        throw new Error(
                            "Product typeof unrecognized: " + typeof product,
                        );
                        // return productFound
                    }
                } else {
                    const sound = new Audio(alertAudio);
                    sound
                        .play()
                        .catch((error) =>
                            console.error(
                                "Error al reproducir el sonido:",
                                error,
                            ),
                        );
                    throw new Error("Product not found");
                }
            } catch (e) {
                let quantity = 1;
                // PESO VARIABLE:
                let barcode =
                    typeof product === "string" || typeof product === "number"
                        ? product.toString()
                        : product.barcode;
                // NOTE: '10' para productos de prueba de Snapei:
                if (
                    typeof barcode === "string" &&
                    ["21", "27", "28", "29"].includes(barcode.substring(0, 2))
                ) {
                    let newBarCode = ["21", "27", "28", "29"].includes(
                        barcode.substring(0, 2),
                    )
                        ? barcode.substring(0, 7)
                        : barcode.substring(0, 6);
                    quantity = parseFloat(
                        barcode.substring(
                            barcode.length - 6,
                            barcode.length - 4,
                        ) +
                            "." +
                            barcode.substring(
                                barcode.length - 4,
                                barcode.length - 1,
                            ),
                    );

                    let response = await fetchWrapper.get(
                        `${baseUrl}/api/v1/management/stores/${storeId}/products/${newBarCode}`,
                    );
                    if (response) {
                        if (response.und.toUpperCase().trim() == "UND") {
                            quantity = quantity * 1000;
                            if (quantity > 100) {
                                quantity = 1;
                            }
                        } // fix, si encuentra
                        const findCategory = recategorizeData.find(
                            (data) => data.category === response.category,
                        );
                        const newCategory =
                            findCategory && findCategory.group
                                ? findCategory.group
                                : response.category;
                        const findImage = categoriesImages.find(
                            (data) => data.category === newCategory,
                        );
                        const categoryImage =
                            findImage && findImage.image
                                ? findImage.image
                                : productImage;

                        // aquí aplico los descuentos distintos, para luego aplicar al precio el mayor
                        let price_with_discount =
                            parseFloat(response.discount) > 0
                                ? parseFloat(response.price) *
                                  (1 - parseFloat(response.discount) / 100)
                                : response.price;
                        let price_with_price_discount =
                            parseFloat(response.price_discount) > 0
                                ? parseFloat(response.price) -
                                  parseFloat(response.price_discount)
                                : response.price;

                        let offer_price =
                            price_with_discount < price_with_price_discount
                                ? price_with_discount
                                : price_with_price_discount;
                        let offer_type =
                            price_with_discount < price_with_price_discount
                                ? "discount"
                                : "price_discount";

                        const productFound = {
                            pk: generateUUID(),
                            id: response.id,
                            sku: response.sku,
                            barcode: response.ean,
                            readed_code: barcode,
                            name: response.name,
                            categories: [findCategory?.group],
                            price: Math.round(response.price),
                            offer_price: Math.round(offer_price),
                            offer_type: offer_type,
                            offer_card_price: Math.round(
                                parseFloat(response.discount_card) -
                                    parseFloat(response.discount) >
                                    0
                                    ? parseFloat(response.price) *
                                          (1 -
                                              (parseFloat(
                                                  response.discount_card,
                                              ) -
                                                  parseFloat(
                                                      response.discount,
                                                  )) /
                                                  100)
                                    : 0,
                            ),
                            qty: quantity ?? 1,
                            tax: parseFloat(response.iva),
                            unit: response.und.toUpperCase().trim(),
                            price_discount:
                                parseFloat(response.price_discount) ?? 0,
                            discount: parseFloat(response.discount) ?? 0,
                            discount_card:
                                parseFloat(response.discount_card) ?? 0,
                            discount_percent:
                                parseFloat(response.discount_percent) ?? 0,
                            age_restriction: response.age_restriction ?? false,
                            image: categoryImage,
                            applied_promos: [],
                            promo_discount: 0,
                            promo_price: 0,
                            added: new Date(),
                        };
                        console.info("productFound!", productFound);
                        if (
                            typeof product === "string" ||
                            typeof product === "number"
                        ) {
                            if (returnItem) {
                                return productFound;
                            }
                            this.addToCart(productFound, fromRecover);
                        } else {
                            throw new Error(
                                "Product typeof unrecognized: " +
                                    typeof product,
                            );
                            // return productFound
                        }
                    } else {
                        const sound = new Audio(alertAudio);
                        sound
                            .play()
                            .catch((error) =>
                                console.error(
                                    "Error al reproducir el sonido:",
                                    error,
                                ),
                            );

                        // TODO: Mensaje de error advirtiendo que no se encontró el item buscado
                        console.error(
                            "Product not found1: ",
                            barcode.toString(),
                        );
                        throw new Error("Product not found");
                    }
                } else {
                    const sound = new Audio(alertAudio);
                    sound
                        .play()
                        .catch((error) =>
                            console.error(
                                "Error al reproducir el sonido:",
                                error,
                            ),
                        );

                    // TODO: Mensaje de error advirtiendo que no se encontró el item buscado
                    console.error("Product not found2: ", product);
                    throw new Error("Product not found");
                }
            }
        },
        setActiveCard(card?: object) {
            console.log("activeCard: ", card);
            this.activeCard = card;
        },
        setStartTime(startTime: Date) {
            this.startTime = startTime;
        },
        setHasGlobalPromotion(hasGlobalPromotion: boolean) {
            this.hasGlobalPromotion = hasGlobalPromotion;
        },
        setPaymentMethod(paymentMethod?: string) {
            this.paymentMethod = paymentMethod;
            if (paymentMethod === "Other") {
                this.activeCard = null;
            }
        },
        async addToCart(item: any, fromRecover?: boolean) {
            let foundProduct = null;
            const updateCart = map(this.cart, (product: any) => {
                if (product.id === item.id) {
                    product.qty = item.qty; // Update qty with item.qty (incoming qty)
                    product.added = new Date();
                    foundProduct = product;
                    return product;
                }
                return product;
            });
            if (foundProduct) {
                this.cart = updateCart;
                const sound = new Audio(successBeep);
                sound
                    .play()
                    .catch((error) =>
                        console.error("Error al reproducir el sonido:", error),
                    );
                this.updateTotals();
                return foundProduct;
            } else {
                const product = {
                    ...item,
                    pk: generateUUID(),
                    added: new Date(),
                };
                this.cart = [...this.cart, product];
                this.products?.forEach((product: any) => {
                    if (product.id === item.id) {
                        product.qty = item.qty;
                        product.added = new Date();
                    }
                });

                if (!fromRecover) {
                    const sound = new Audio(successBeep);
                    sound
                        .play()
                        .catch((error) =>
                            console.error(
                                "Error al reproducir el sonido:",
                                error,
                            ),
                        );
                }
                this.updateTotals();
                if (item.age_restriction) {
                    this.hasRestriction = true;
                }
                return product;
            }
        },
        //qty
        async incrementQty(productPk: number) {
            let foundProduct = null;
            const updateCart = map(this.cart, (product: any) => {
                if (product.pk === productPk) {
                    foundProduct = product;
                    return {
                        ...product,
                        qty: product.qty + 1,
                    };
                }
                return product;
            });
            this.cart = updateCart;

            const sound = new Audio(beepAudio);
            sound
                .play()
                .catch((error) =>
                    console.error("Error al reproducir el sonido:", error),
                );

            this.updateTotals();
            return foundProduct;
        },
        //qty
        decrementQty(productPk: any) {
            try {
                // console.log("into decrementQty")
                let foundProduct = null;
                // console.log("cart length antes:", this.cart.length, this.cart)
                const updateCart = map(this.cart, (product: any) => {
                    // console.log("product: ", product.pk, "==", productPk, "?", (product.pk === productPk) )
                    if (product.pk === productPk) {
                        foundProduct = product;
                        return {
                            ...product,
                            qty: product.qty - 1,
                        };
                    }
                    return { ...product };
                });
                // console.log("cart length despues:", updateCart.length, updateCart)
                this.cart = updateCart;

                const sound = new Audio(popBeep);
                sound
                    .play()
                    .catch((error) =>
                        console.error("Error al reproducir el sonido:", error),
                    );

                this.updateTotals();
                return foundProduct;
            } catch (error) {
                throw error;
            }
        },

        updateQty(productPk: any, qty: number) {
            try {
                let foundProduct = null;
                const updateCart = map(this.cart, (product: any) => {
                    if (product.pk === productPk) {
                        foundProduct = product;
                        return {
                            ...product,
                            qty: qty,
                        };
                    }
                    return { ...product };
                });
                this.cart = updateCart;
                const sound = new Audio(beepAudio);
                sound
                    .play()
                    .catch((error) =>
                        console.error("Error al reproducir el sonido:", error),
                    );

                this.updateTotals();
                return foundProduct;
            } catch (error) {
                throw error;
            }
        },

        async updateTotals() {
            let subtotal = 0;
            let discount = 0;
            let discount_card = 0;
            let discount_percent = 0;
            let taxes = 0;
            let total_qty = 0;

            await this.cart.forEach(async (product: any) => {
                const sale_price_total = Math.round(
                    parseFloat(product.price) * parseInt(product.qty),
                );

                total_qty += parseInt(product.qty);

                const offer_price_total = Math.round(
                    (product.offer_price ?? 0) * parseInt(product.qty),
                );
                discount +=
                    product.offer_price > 0
                        ? Math.round(sale_price_total - offer_price_total)
                        : 0;

                taxes += Math.round(
                    sale_price_total * (parseFloat(product.iva) / 100),
                );

                subtotal += sale_price_total - taxes;
            });

            // Asignamos los valores a las propiedades correspondientes
            this.subTotal = Math.round(subtotal);
            this.discount = Math.round(discount);
            this.discount_card = Math.round(discount_card);
            this.discount_percent = Math.round(discount_percent);
            this.taxes = Math.round(taxes);
            this.total = Math.round(subtotal + taxes - discount);
            this.total_qty = total_qty;
        },

        // delete Product
        async deleteProduct(productPk: any) {
            // console.log("Entra a deleteProduct()");
            const updateCart = filter(
                this.cart,
                (p: any) => p.pk !== productPk,
            );
            this.cart = updateCart;

            const sound = new Audio(popBeep);
            sound
                .play()
                .catch((error) =>
                    console.error("Error al reproducir el sonido:", error),
                );

            if (this.cart.length === 0) {
                this.deleteCart("deleteItem");
            } else {
                this.updateTotals();
            }
        },
        // delete Cart
        async deleteCart(from = "") {
            // console.log(`Entra a deleteCart(from=${from})`);
            this.cart = [];
            if (from !== "deleteItem") {
                useInvoiceStore().setInvoice("");
                useInvoiceStore().setPaymentStatus("");
                this.hasGlobalPromotion = false;
                this.startTime = new Date();
            }
            this.updateTotals();
        },

        async applyPromotionsToCart(promotions: any) {
            // bestPromotion
            const subtotal = toRaw(this.subTotal);
            for (const product of this.cart as Products[]) {
                product.applied_promos = promotions;
                var cumulative_discount = 0;
                for (const promotion of promotions) {
                    // TODO: Pueden llegar varios descuentos al tiempo?
                    cumulative_discount += promotion.value;
                    if (promotion.type === "percent") {
                        product.promo_discount = cumulative_discount;
                    } else if (promotion.type === "value") {
                        product.promo_price += promotion.value;
                    }
                }
            }
            console.log("antes de updateTotals()", this.cart);
            await this.updateTotals();
            this.hasGlobalPromotion = true;
            console.log(
                "subtotal (before):",
                subtotal,
                "new total: ",
                this.total,
            );
            return (subtotal ?? 0) - (this.total ?? 0);
        },

        findProductOnCart(code: string) {
            return this.cart.find((product: any) => product.barcode === code);
        },
        async createPayment(payload: any) {
            const baseUrl = useRuntimeConfig().public.apiBase;
            return fetchWrapper
                .post(`${baseUrl}/api/v1/payments/mercado-pago/`, payload)
                .then((data) => {
                    console.info(data);
                    return {
                        message: data.message.message ?? data.message,
                        status: data.status ?? data.message.status,
                        id: data.message?.id,
                    };
                })
                .catch((error) => {
                    throw error;
                });
        },

        async getTimers() {
            const startTime = this.startTime
                ? new Date(this.startTime)
                : new Date();
            const now = new Date();
            const timeInteraction = (now - startTime) / 1000;
            let timeDiffFirstProduct = 0;
            let timeDiffLastProduct = 0;
            let averageInterval = 0;

            let products = JSON.parse(JSON.stringify(this.cart)).filter(
                (product: Products) => !product.categories.includes("BOLSAS"),
            );
            if (products.length > 0) {
                await products.forEach(async (product: Products) => {
                    product.added =
                        typeof product.added === "object"
                            ? product.added
                            : new Date(product.added); // Convertir string ISO a Date
                });
                await products.sort(
                    async (a, b) => a.added.getTime() - b.added.getTime(),
                );
                const firstProduct = products[0];
                const lastProduct = products[products.length - 1];
                timeDiffFirstProduct =
                    (firstProduct.added.getTime() - startTime.getTime()) / 1000;
                const timeDiffLastProduct =
                    (now.getTime() - lastProduct.added.getTime()) / 1000;

                const timeIntervals = await Promise.all(
                    products.map((product, index) => {
                        if (index === 0) return 0; // No hay intervalo para el primer producto
                        return (
                            (new Date(product.added).getTime() -
                                new Date(products[index - 1].added).getTime()) /
                            1000
                        );
                    }),
                );
                averageInterval =
                    timeIntervals.slice(1).reduce((a, b) => a + b, 0) /
                    (products.length - 1);
            }

            return {
                interaction_time: Math.round(timeInteraction),
                first_product_time: Math.round(timeDiffFirstProduct),
                last_product_time: Math.round(timeDiffLastProduct),
                average_product_time: Math.round(averageInterval ?? 0),
            };
        },

        // Fetch Customers from addresses
        // async fetchAddress() {
        //     try {
        //         const data = await axios.get('/api/address/list');
        //         this.addresses = data.data;
        //     } catch (error) {
        //         alert(error);
        //         console.log(error);
        //     }
        // },
        //select gender

        //select category
        SelectCategory(items: any) {
            this.category = items;
        },
        SelectGender(items: any) {
            this.gender = items;
        },
        SelectPrice(price: any) {
            this.price = price;
        },
        setPagination(page: number) {
            this.page = page;
        },
        setPagesize(size: number) {
            this.pageSize = size;
        },
        setPageCount(count: number) {
            this.pageCount = count;
        },
        setFilterDiscount(status: boolean | null) {
            this.filterDiscount = status;
        },
        sortByColor(itemcolor: string) {
            this.color = itemcolor;
        },
        setSortBy(sortBy: string | null | undefined) {
            this.sortBy = sortBy;
        },
        setSearch(search: string | null) {
            console.log("seteando search: ", search);
            this.search = search;
        },
        setStock(stock: boolean | null) {
            this.stock = stock;
        },
        setLayout(layout: string) {
            this.layout = layout;
        },

        getsubTotal() {
            this.subTotal = sum(
                this.cart.map((product: any) => product.price * product.qty),
            );
        },
        //total
        getTotal() {
            this.total = this.subTotal - (this.discount ?? 0);
        },
        //discount
        getDiscount() {
            this.discount = Math.round(this.subTotal * (5 / 100));
        },
        //select Price
        filterReset() {
            this.color = "All";
            this.price = null;
            this.gender = "";
            this.category = [];
            this.sortBy = null;
            this.search = null;
            this.page = 1;
            this.pageSize = 20;
            this.pageCount = 1;
            this.filterDiscount = null;
            this.stock = null;
        },
    },
    persist: {
        storage: persistedState.localStorage,
    },
});
